import {useEffect, useRef, useState} from "react";
import {Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useChangePassword} from "../../api/settings/settingsQueries";
import ArenaSnackBar from "../ArenaSnackBar";

type ChangePasswordDraft = {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
};

const initialChangePasswordDraft: ChangePasswordDraft = {
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
};

type ChangePasswordDialogProps = {
    open: boolean;
    onClose: () => void;
};

const ChangePasswordDialog: React.FC<ChangePasswordDialogProps> = ({open, onClose}) => {
    const {t: translate} = useTranslation();
    const changePasswordMutation = useChangePassword();
    const [changePasswordDraft, setChangePasswordDraft] = useState(initialChangePasswordDraft);
    const wasOpenRef = useRef(open);

    useEffect(() => {
        if (open && !wasOpenRef.current) {
            setChangePasswordDraft(initialChangePasswordDraft);
            changePasswordMutation.reset();
        }

        wasOpenRef.current = open;
    }, [changePasswordMutation, open]);

    const isPasswordMismatch =
        changePasswordDraft.confirmPassword.length > 0
        && changePasswordDraft.newPassword !== changePasswordDraft.confirmPassword;
    const isChangePasswordSubmitDisabled =
        changePasswordMutation.isPending
        || !changePasswordDraft.currentPassword
        || !changePasswordDraft.newPassword
        || !changePasswordDraft.confirmPassword
        || isPasswordMismatch;

    const handleDialogClose = () => {
        if (changePasswordMutation.isPending) {
            return;
        }

        onClose();
        setChangePasswordDraft(initialChangePasswordDraft);
        changePasswordMutation.reset();
    };

    const handleFieldChange =
        (field: keyof ChangePasswordDraft) => (event: React.ChangeEvent<HTMLInputElement>) => {
            setChangePasswordDraft((previousDraft) => ({
                ...previousDraft,
                [field]: event.target.value,
            }));
        };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        if (isChangePasswordSubmitDisabled) {
            return;
        }

        try {
            await changePasswordMutation.mutateAsync({
                oldPassword: changePasswordDraft.currentPassword,
                newPassword: changePasswordDraft.newPassword,
            });
            handleDialogClose();
        } catch (error) {
            console.error(error);
        }
    };

    const handleErrorClose = () => {
        changePasswordMutation.reset();
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={changePasswordMutation.isPending ? undefined : handleDialogClose}
                fullWidth
                maxWidth="xs"
            >
                <Box component="form" onSubmit={handleSubmit}>
                    <DialogTitle>{translate("settings.changePasswordDialog.title")}</DialogTitle>
                    <DialogContent
                        sx={{
                            display: "grid",
                            gap: 2,
                            "&.MuiDialogContent-root": {
                                pt: 1,
                            },
                        }}
                    >
                        <TextField
                            label={translate("settings.changePasswordDialog.currentPassword")}
                            type="password"
                            value={changePasswordDraft.currentPassword}
                            onChange={handleFieldChange("currentPassword")}
                            autoComplete="current-password"
                            fullWidth
                            disabled={changePasswordMutation.isPending}
                            autoFocus
                        />
                        <TextField
                            label={translate("settings.changePasswordDialog.newPassword")}
                            type="password"
                            value={changePasswordDraft.newPassword}
                            onChange={handleFieldChange("newPassword")}
                            autoComplete="new-password"
                            fullWidth
                            disabled={changePasswordMutation.isPending}
                        />
                        <TextField
                            label={translate("settings.changePasswordDialog.confirmPassword")}
                            type="password"
                            value={changePasswordDraft.confirmPassword}
                            onChange={handleFieldChange("confirmPassword")}
                            autoComplete="new-password"
                            fullWidth
                            disabled={changePasswordMutation.isPending}
                            error={isPasswordMismatch}
                            helperText={
                                isPasswordMismatch
                                    ? translate("settings.changePasswordDialog.passwordsDoNotMatch")
                                    : " "
                            }
                        />
                    </DialogContent>
                    <DialogActions sx={{px: 3, pb: 2.5}}>
                        <Button onClick={handleDialogClose} disabled={changePasswordMutation.isPending}>
                            {translate("common.cancel")}
                        </Button>
                        <Button type="submit" variant="contained" disabled={isChangePasswordSubmitDisabled}>
                            {translate("settings.changePasswordDialog.submit")}
                        </Button>
                    </DialogActions>
                </Box>
            </Dialog>
            <ArenaSnackBar message={changePasswordMutation.error?.message} onClose={handleErrorClose} />
        </>
    );
};

export default ChangePasswordDialog;
